import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";
import VideoThumbnail from "../Video/VideoThumbnail";

function TrainingTabAllCoursesPage() {
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError(null);

        const jwt = await getVerifiedJWT();
        if (!jwt) {
          setCourses([]);
          return;
        }

        const response = await fetch(getApiEndpoint("/v1/training/list"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          cache: "no-cache",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch training courses");
        }

        const result = await response.json();
        setCourses(result?.data?.courses || []);
      } catch (err) {
        setError(err.message || "Failed to fetch training courses");
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [getVerifiedJWT]);

  const filteredCourses = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return courses;

    return courses.filter(course =>
      (course.course_title || "").toLowerCase().includes(query)
    );
  }, [courses, searchQuery]);

  if (loading) {
    return (
      <div className="training-page-no-courses-message">Loading courses…</div>
    );
  }

  if (error) {
    return (
      <DisplayMessage
        type="error"
        message={`Error loading courses: ${error}`}
      />
    );
  }

  return (
    <div className="training-page-all-courses-view">
      <div className="training-page-all-videos-header">
        <div>
          <h2 className="training-page-all-videos-title">All Courses</h2>
          <p className="training-page-all-videos-count">
            {filteredCourses.length} course
            {filteredCourses.length !== 1 ? "s" : ""} available
          </p>
        </div>
      </div>

      <div className="training-page-all-videos-controls">
        <div className="training-page-all-videos-search">
          <svg
            className="training-page-all-videos-search-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="training-page-all-videos-search-input"
          />
        </div>
      </div>

      {filteredCourses.length > 0 ? (
        <div className="training-page-all-courses-four-grid">
          {filteredCourses.map(course => {
            const progressPct =
              course.total_video_count > 0
                ? Math.round(
                    (course.completed_video_count / course.total_video_count) *
                      100
                  )
                : 0;

            return (
              <div
                key={course.id}
                className="training-page-course-card-small"
                onClick={() => navigate(`/course/${course.id}`)}
              >
                <div className="training-page-course-card-thumbnail">
                  <VideoThumbnail
                    videoUrl={course.video_url}
                    thumbnailUrl={course.course_thumbnail}
                    alt={course.course_title}
                    className="training-page-course-card-image"
                  />

                  {course.completed_video_count > 0 &&
                    course.completed_video_count < course.total_video_count && (
                      <div className="training-page-course-card-badge progress">
                        {progressPct}%
                      </div>
                    )}

                  {course.completed_video_count === course.total_video_count &&
                    course.total_video_count > 0 && (
                      <div className="training-page-course-card-badge watched">
                        Watched
                      </div>
                    )}
                </div>

                <div className="training-page-course-card-content">
                  <h3 className="training-page-course-card-title">
                    {course.course_title}
                  </h3>
                  <div className="training-page-course-card-meta">
                    <span className="training-page-course-card-videos">
                      {course.total_video_count} video
                      {course.total_video_count !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="training-page-no-courses-message">
          No courses found matching your search.
        </div>
      )}
    </div>
  );
}

export default TrainingTabAllCoursesPage;
